/**
 * TerrainErosion — the three games-standard erosion models over a row-major
 * `resolution × resolution` heightfield in world units:
 *
 *  - Droplet hydraulic erosion (Beyer 2015, TU München — the erodr/Lague
 *    particle model): seeded rain droplets descend the gradient, eroding
 *    below capacity and depositing above it. Carves gullies and dendritic
 *    wash at the meso scale.
 *  - Thermal erosion (Musgrave 1989): material on slopes steeper than the
 *    talus (repose) angle slips to lower neighbours until the critical
 *    angle is restored. Turns cliffs into scree aprons. Mass-conserving.
 *  - Stream-power incision (E = K·A^m·S) with the Braun & Willett 2013
 *    implicit solver: unconditionally stable, one O(n) pass per step over
 *    the drainage tree from TerrainHydrology. Carves river valleys at the
 *    macro scale.
 *
 * All functions mutate `height` IN PLACE and are deterministic for a given
 * seed/config. Pure TypeScript — no Three.js, testable in Node.
 *
 * A note on units: heights and cellSize are world units (metres). Droplet
 * gradients are per-CELL (the classic formulation) so its tuning constants
 * are resolution-relative; thermal and stream-power use physical slopes.
 */

import { analyzeHydrology } from './TerrainHydrology.js';
import { activeHydrologyKernel } from './HydrologyKernel.js';

const SQRT2_ = Math.SQRT2;

// ─── Deterministic RNG ──────────────────────────────────────────────────────

/** mulberry32 — small deterministic PRNG (no Math.random anywhere). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ─── Droplet hydraulic erosion ──────────────────────────────────────────────

export interface DropletErosionConfig {
  /** Number of simulated droplets. Default 40 per row (resolution × 40). */
  droplets?: number;
  /** RNG seed. Default 1337. */
  seed?: number;
  /** Direction inertia 0..1 (0 = pure gradient descent). Default 0.05. */
  inertia?: number;
  /** Sediment capacity multiplier. Default 4. */
  capacity?: number;
  /** Minimum slope for capacity (prevents dead zones on flats). Default 0.01. */
  minSlope?: number;
  /** Fraction of free capacity eroded per step. Default 0.3. */
  erodeRate?: number;
  /** Fraction of excess sediment deposited per step. Default 0.3. */
  depositRate?: number;
  /** Water evaporated per step (0..1). Default 0.015. */
  evaporate?: number;
  /** Gravity for the speed update. Default 4. */
  gravity?: number;
  /** Maximum droplet lifetime in steps. Default 48. */
  maxLifetime?: number;
  /** Erosion brush radius in cells. Default 3. */
  radius?: number;
}

/** Bilinear height at fractional grid coords. */
function sampleBilinear(height: Float32Array, n: number, x: number, z: number): number {
  const xi = Math.floor(x);
  const zi = Math.floor(z);
  const fx = x - xi;
  const fz = z - zi;
  const i = zi * n + xi;
  const h00 = height[i];
  const h10 = height[i + 1];
  const h01 = height[i + n];
  const h11 = height[i + n + 1];
  return h00 * (1 - fx) * (1 - fz) + h10 * fx * (1 - fz) + h01 * (1 - fx) * fz + h11 * fx * fz;
}

/**
 * Droplet hydraulic erosion (in place). Heights in world units; erosion and
 * deposition amounts are in the same units.
 *
 * The dynamics run on a range-normalized view of the field: Beyer's tuning
 * constants are calibrated for [0,1] relief, and raw metre-scale heights
 * blow up the capacity term (converging droplets then compound
 * self-amplifying craters tens of metres deep). Normalizing keeps every
 * droplet's total impact relief-relative regardless of input units.
 */
export function erodeDroplets(height: Float32Array, resolution: number, config: DropletErosionConfig = {}): void {
  // Deliberately NOT routed to the wasm kernel: measured 1.06× (see
  // HydrologyKernel.ts) — thermalErode and analyzeHydrology are.
  const n = resolution;
  const droplets = Math.max(0, Math.floor(config.droplets ?? n * 40));
  const inertia = config.inertia ?? 0.05;
  const capacityFactor = config.capacity ?? 4;
  const minSlope = config.minSlope ?? 0.01;
  const erodeRate = config.erodeRate ?? 0.3;
  const depositRate = config.depositRate ?? 0.3;
  const evaporate = config.evaporate ?? 0.015;
  const gravity = config.gravity ?? 4;
  const maxLifetime = config.maxLifetime ?? 48;
  const radius = Math.max(1, Math.floor(config.radius ?? 3));
  const rand = mulberry32(config.seed ?? 1337);

  // Range-normalize (see the doc comment): dynamics on [0,1] relief.
  let hMin = Infinity;
  let hMax = -Infinity;
  for (let i = 0; i < height.length; i++) {
    if (height[i] < hMin) hMin = height[i];
    if (height[i] > hMax) hMax = height[i];
  }
  const hScale = hMax - hMin;
  if (!(hScale > 0)) return; // flat field: nothing to erode
  for (let i = 0; i < height.length; i++) height[i] = (height[i] - hMin) / hScale;

  // Precomputed brush: offsets + normalized (1 − d/r) weights.
  const brushOffX: number[] = [];
  const brushOffZ: number[] = [];
  const brushW: number[] = [];
  {
    let sum = 0;
    for (let bz = -radius; bz <= radius; bz++) {
      for (let bx = -radius; bx <= radius; bx++) {
        const d = Math.sqrt(bx * bx + bz * bz);
        if (d > radius) continue;
        const w = 1 - d / radius;
        brushOffX.push(bx);
        brushOffZ.push(bz);
        brushW.push(w);
        sum += w;
      }
    }
    for (let i = 0; i < brushW.length; i++) brushW[i] /= sum;
  }

  for (let d = 0; d < droplets; d++) {
    let px = rand() * (n - 2);
    let pz = rand() * (n - 2);
    let dirX = 0;
    let dirZ = 0;
    let speed = 1;
    let water = 1;
    let sediment = 0;

    for (let life = 0; life < maxLifetime; life++) {
      const xi = Math.floor(px);
      const zi = Math.floor(pz);
      const fx = px - xi;
      const fz = pz - zi;
      const i = zi * n + xi;

      // Bilinear gradient (height units per cell).
      const h00 = height[i];
      const h10 = height[i + 1];
      const h01 = height[i + n];
      const h11 = height[i + n + 1];
      const gradX = (h10 - h00) * (1 - fz) + (h11 - h01) * fz;
      const gradZ = (h01 - h00) * (1 - fx) + (h11 - h10) * fx;
      const hOld = h00 * (1 - fx) * (1 - fz) + h10 * fx * (1 - fz) + h01 * (1 - fx) * fz + h11 * fx * fz;

      // Blend direction with the downhill gradient (Beyer eq. 3.4).
      dirX = dirX * inertia - gradX * (1 - inertia);
      dirZ = dirZ * inertia - gradZ * (1 - inertia);
      const len = Math.sqrt(dirX * dirX + dirZ * dirZ);
      if (len < 1e-9) break; // flat + no momentum: the droplet dies here
      dirX /= len;
      dirZ /= len;
      px += dirX;
      pz += dirZ;
      if (px < 0 || px >= n - 1 || pz < 0 || pz >= n - 1) break;

      const hNew = sampleBilinear(height, n, px, pz);
      const dh = hNew - hOld;

      // Capacity ∝ slope · speed · water (Beyer eq. 5.2).
      const cap = Math.max(-dh, minSlope) * speed * water * capacityFactor;

      if (sediment > cap || dh > 0) {
        // Deposit: fill the pit when climbing, else shed the excess.
        const amount = dh > 0 ? Math.min(dh, sediment) : (sediment - cap) * depositRate;
        sediment -= amount;
        // Bilinear deposition at the OLD cell corners keeps surfaces smooth.
        height[i] += amount * (1 - fx) * (1 - fz);
        height[i + 1] += amount * fx * (1 - fz);
        height[i + n] += amount * (1 - fx) * fz;
        height[i + n + 1] += amount * fx * fz;
      } else {
        // Erode over the brush — never dig deeper than the drop itself,
        // which would carve spikes/holes along the path.
        const amount = Math.min((cap - sediment) * erodeRate, -dh);
        for (let b = 0; b < brushW.length; b++) {
          const bx = xi + brushOffX[b];
          const bz = zi + brushOffZ[b];
          if (bx < 0 || bx >= n || bz < 0 || bz >= n) continue;
          // No floor clamp: heights may be legitimately negative (sea beds).
          const take = amount * brushW[b];
          const bi = bz * n + bx;
          height[bi] -= take;
          sediment += take;
        }
      }

      speed = Math.sqrt(Math.max(0, speed * speed - dh * gravity));
      water *= 1 - evaporate;
      if (water < 1e-3) break;
    }
  }

  // Back to world units.
  for (let i = 0; i < height.length; i++) height[i] = height[i] * hScale + hMin;
}

// ─── Thermal erosion ────────────────────────────────────────────────────────

export interface ThermalErosionConfig {
  /** Relaxation iterations. Default 50. */
  iterations?: number;
  /** Critical slope tan(repose angle), Δh per metre. Default tan(33°) ≈ 0.65. */
  talusSlope?: number;
  /** Fraction of the excess moved per iteration (stability ≤ 0.5). Default 0.5. */
  amount?: number;
  /** Cell size in world units. Default 1. */
  cellSize?: number;
}

const THERMAL_NX = [1, -1, 0, 0, 1, -1, 1, -1] as const;
const THERMAL_NZ = [0, 0, 1, -1, 1, 1, -1, -1] as const;

/**
 * Thermal (talus) erosion in place. Material slips from any cell to each
 * lower neighbour whose slope exceeds the repose angle, proportionally to
 * the excess. Jacobi-style delta buffer ⇒ order-independent and
 * mass-conserving (Σh is preserved to float precision).
 */
export function thermalErode(height: Float32Array, resolution: number, config: ThermalErosionConfig = {}): void {
  // Wasm kernel when installed (bit-identical; see HydrologyKernel.ts).
  const kernel = activeHydrologyKernel();
  if (kernel && kernel.thermalErode(height, resolution, config)) return;
  const n = resolution;
  const iterations = Math.max(1, Math.floor(config.iterations ?? 50));
  const talus = config.talusSlope ?? Math.tan((33 * Math.PI) / 180);
  const amount = Math.min(0.5, Math.max(0, config.amount ?? 0.5));
  const cellSize = config.cellSize ?? 1;
  const delta = new Float32Array(n * n);

  for (let it = 0; it < iterations; it++) {
    delta.fill(0);
    for (let z = 0; z < n; z++) {
      for (let x = 0; x < n; x++) {
        const i = z * n + x;
        const h = height[i];
        // Gather neighbours above threshold; distribute ∝ excess.
        let totalExcess = 0;
        let maxExcess = 0;
        for (let k = 0; k < 8; k++) {
          const nx = x + THERMAL_NX[k];
          const nz = z + THERMAL_NZ[k];
          if (nx < 0 || nx >= n || nz < 0 || nz >= n) continue;
          const dist = (k < 4 ? 1 : SQRT2_) * cellSize;
          const excess = h - height[nz * n + nx] - talus * dist;
          if (excess > 0) {
            totalExcess += excess;
            if (excess > maxExcess) maxExcess = excess;
          }
        }
        if (totalExcess <= 0) continue;
        // Move half the worst overage (classic stability bound), split
        // across the violating neighbours by their share of the excess.
        const move = amount * maxExcess;
        for (let k = 0; k < 8; k++) {
          const nx = x + THERMAL_NX[k];
          const nz = z + THERMAL_NZ[k];
          if (nx < 0 || nx >= n || nz < 0 || nz >= n) continue;
          const dist = (k < 4 ? 1 : SQRT2_) * cellSize;
          const ni = nz * n + nx;
          const excess = h - height[ni] - talus * dist;
          if (excess > 0) {
            const share = (move * excess) / totalExcess;
            delta[i] -= share;
            delta[ni] += share;
          }
        }
      }
    }
    for (let i = 0; i < delta.length; i++) height[i] += delta[i];
  }
}

// ─── Stream-power incision ──────────────────────────────────────────────────

export interface StreamPowerConfig {
  /** Solver steps (each re-routes drainage). Default 8. */
  iterations?: number;
  /** Erodibility constant K. Default 0.02. */
  erodibility?: number;
  /** Drainage-area exponent m (concavity = m/n with n = 1). Default 0.5. */
  areaExponent?: number;
  /** Time step per iteration (arbitrary units; implicit ⇒ any size is stable). Default 1. */
  dt?: number;
  /** Cell size in world units. Default 1. */
  cellSize?: number;
  /** Uniform uplift added per step (keeps relief alive over many steps). Default 0. */
  uplift?: number;
}

/**
 * Stream-power fluvial incision (in place): E = K·A^m·S with n = 1, solved
 * with the Braun & Willett (2013) implicit scheme — walk the drainage tree
 * from outlets upward so each cell relaxes against its receiver's ALREADY
 * UPDATED height:  h′ = (h + f·h′_recv) / (1 + f),  f = K·A^m·dt / dist.
 * Unconditionally stable; never cuts a cell below its receiver.
 */
export function streamPowerErode(height: Float32Array, resolution: number, config: StreamPowerConfig = {}): void {
  const n = resolution;
  const total = n * n;
  const iterations = Math.max(1, Math.floor(config.iterations ?? 8));
  const k = config.erodibility ?? 0.02;
  const m = config.areaExponent ?? 0.5;
  const dt = config.dt ?? 1;
  const cellSize = config.cellSize ?? 1;
  const uplift = config.uplift ?? 0;
  const cellArea = cellSize * cellSize;

  for (let it = 0; it < iterations; it++) {
    if (uplift !== 0) {
      // Borders stay pinned (base level) — the classic boundary condition.
      for (let z = 1; z < n - 1; z++) {
        for (let x = 1; x < n - 1; x++) height[z * n + x] += uplift * dt;
      }
    }

    const { receivers, accumulation, order } = analyzeHydrology(height, resolution);

    // Ascending order = outlets first ⇒ receivers are solved before donors.
    for (let idx = total - 1; idx >= 0; idx--) {
      const i = order[idx];
      const r = receivers[i];
      if (r < 0) continue; // outlet: base level, never incised
      const dxc = Math.abs((r % n) - (i % n));
      const dzc = Math.abs((((r / n) | 0) - ((i / n) | 0)));
      const dist = (dxc + dzc === 2 ? SQRT2_ : 1) * cellSize;
      const f = (k * Math.pow(accumulation[i] * cellArea, m) * dt) / dist;
      height[i] = (height[i] + f * height[r]) / (1 + f);
    }
  }
}
